/**
 * Delphi DisplayFormat / Database_Yonetim.MASKE biçimlerinin web karşılığı.
 *  '#,#0.00'  → binlik ayraçlı 2 ondalık (tr-TR: 1.234,56)
 *  '#,#0.0000'→ 4 ondalık
 *  '#,#0.'    → binlik ayraçlı tam sayı
 *  'hh:mm:ss' → saat
 *  'dd.mm.yyyy' / 'dd/mm/yyyy' → tarih
 */
const nfCache = new Map<string, Intl.NumberFormat>();
function nf(min: number, max: number, group = true) {
  const k = `${min}.${max}.${group}`;
  let f = nfCache.get(k);
  if (!f) {
    f = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: min, maximumFractionDigits: max, useGrouping: group });
    nfCache.set(k, f);
  }
  return f;
}

export function maskDecimals(mask: string): { min: number; max: number; group: boolean } | null {
  const m = /^[#,0]*0?(?:\.(0*)(#*))?$/.exec(mask.replace(/;.*$/, '').trim());
  if (!m) return null;
  const req = m[1]?.length ?? 0;
  const opt = m[2]?.length ?? 0;
  return { min: req, max: req + opt, group: mask.includes(',') };
}

const NUMERIC = new Set(['int', 'bigint', 'smallint', 'tinyint', 'float', 'real', 'decimal', 'numeric', 'money', 'smallmoney']);
const DATE = new Set(['datetime', 'smalldatetime', 'date', 'datetime2']);
export const isNumericType = (t?: string) => !!t && NUMERIC.has(t);
export const isDateType = (t?: string) => !!t && DATE.has(t);
export const isBoolType = (t?: string) => t === 'bit';

export function parseIso(v: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(v);
  if (!m) return null;
  return new Date(+m[1], +m[2] - 1, +m[3], +(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0));
}

const pad = (n: number) => String(n).padStart(2, '0');

export function formatDate(v: unknown, withTime: 'auto' | boolean = 'auto'): string {
  if (v == null || v === '') return '';
  const d = typeof v === 'string' ? parseIso(v) : v instanceof Date ? v : null;
  if (!d) return String(v);
  const date = `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  const hasTime = d.getHours() || d.getMinutes() || d.getSeconds();
  if (withTime === true || (withTime === 'auto' && hasTime)) return `${date} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return date;
}

export function formatTime(v: unknown): string {
  if (v == null || v === '') return '';
  const d = typeof v === 'string' ? parseIso(v) : null;
  if (!d) return String(v);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function formatValue(v: unknown, sqlType?: string, mask?: string | null): string {
  if (v == null) return '';
  if (typeof v === 'object' && v && '$binary' in (v as any)) return '[ikili veri]';
  if (mask === 'hh:mm:ss' || mask === 'hh:nn:ss' || mask === 'hh:mm') return formatTime(v);
  if (isDateType(sqlType) || (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v))) return formatDate(v);
  if (isBoolType(sqlType)) return v ? '✓' : '';
  if (typeof v === 'number') {
    if (mask) {
      const d = maskDecimals(mask);
      if (d) return nf(d.min, d.max, d.group).format(v);
    }
    if (sqlType === 'money') return nf(2, 2).format(v);
    if (Number.isInteger(v)) return String(v);
    return nf(0, 6, false).format(v);
  }
  return String(v);
}

/** Kullanıcı girdisini (tr-TR) sayıya çevirir: "1.234,5" → 1234.5 */
export function parseNumberInput(s: string): number | null {
  const t = s.trim().replace(/\s/g, '');
  if (!t) return null;
  const n = Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t);
  return Number.isFinite(n) ? n : null;
}

/** "gg.aa.yyyy [ss:dd]" → ISO (saat dilimsiz) */
export function parseDateInput(s: string): string | null {
  const t = s.trim();
  if (!t) return null;
  const m = /^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})(?:\s+(\d{1,2}):(\d{2}))?$/.exec(t);
  if (m) {
    const y = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    const dt = new Date(y, +m[2] - 1, +m[1], +(m[4] ?? 0), +(m[5] ?? 0));
    // 31.02 gibi takvimde olmayan günler ve geçersiz saatler reddedilir
    if (dt.getFullYear() !== y || dt.getMonth() !== +m[2] - 1 || dt.getDate() !== +m[1] || +(m[4] ?? 0) > 23 || +(m[5] ?? 0) > 59) return null;
    return `${y}-${pad(+m[2])}-${pad(+m[1])}T${pad(+(m[4] ?? 0))}:${pad(+(m[5] ?? 0))}:00`;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.length === 10 ? `${t}T00:00:00` : t;
  return null;
}

export function toInputDate(v: unknown): string {
  if (!v || typeof v !== 'string') return '';
  return v.slice(0, 10);
}

/** Delphi TColor (clRed, $00FF0000 BGR, ondalık BGR) → CSS rengi */
const NAMED: Record<string, string> = {
  clblack: '#000000', clmaroon: '#800000', clgreen: '#008000', clolive: '#808000', clnavy: '#000080', clpurple: '#800080',
  clteal: '#008080', clgray: '#808080', clsilver: '#c0c0c0', clred: '#ff0000', cllime: '#00ff00', clyellow: '#ffff00',
  clblue: '#0000ff', clfuchsia: '#ff00ff', claqua: '#00ffff', clwhite: '#ffffff', clmoneygreen: '#c0dcc0', clskyblue: '#a6caf0',
  clcream: '#fffbf0', clmedgray: '#a0a0a4', clwindow: '#ffffff', clwindowtext: '#000000', clbtnface: '#f0f0f0',
};
export function delphiColor(v: string | null | undefined): string | undefined {
  if (!v) return undefined;
  const s = v.trim();
  if (!s) return undefined;
  const named = NAMED[s.toLowerCase()];
  if (named) return named;
  let n: number | null = null;
  if (/^\$[0-9a-f]+$/i.test(s)) n = parseInt(s.slice(1), 16);
  else if (/^-?\d+$/.test(s)) n = Number(s);
  if (n == null || n < 0) return /^#|^rgb|^[a-z]+$/i.test(s) ? s : undefined;
  const r = n & 0xff, g = (n >> 8) & 0xff, b = (n >> 16) & 0xff;
  return `rgb(${r},${g},${b})`;
}

/** Tarih kısayolları (Delphi rapor parametreleri: Bugün, Dün, Yarın, Aybaşı, Aysonu, Yılbaşı, Yılsonu) */
export function dateShortcut(name: string | undefined | null, now = new Date()): string | null {
  if (!name) return null;
  const k = name.toLocaleLowerCase('tr-TR').replace(/\s/g, '');
  const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00`;
  const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
  switch (k) {
    case 'bugün': case 'bugun': return iso(new Date(y, m, d));
    case 'dün': case 'dun': return iso(new Date(y, m, d - 1));
    case 'yarın': case 'yarin': return iso(new Date(y, m, d + 1));
    case 'aybaşı': case 'aybasi': return iso(new Date(y, m, 1));
    case 'aysonu': return iso(new Date(y, m + 1, 0));
    case 'yılbaşı': case 'yilbasi': return iso(new Date(y, 0, 1));
    case 'yılsonu': case 'yilsonu': return iso(new Date(y, 11, 31));
    default: return null;
  }
}
